
import React, { useState, useEffect, useCallback } from 'react';
import { TrainingJournalHeader } from './components/TrainingJournalHeader';
import { TrainingStats } from './components/TrainingStats';
import { TabsComponent } from './components/TabsComponent';
import { AddTraining } from './components/AddTraining';
import { useIsMobile } from '@/hooks/use-mobile';
import { TimePeriod } from './types';
import { TrainingJournalProvider } from './context/TrainingJournalContext';
import { useTrainingJournal } from './context/useTrainingJournal';
import { ZoomableTimeline } from '@/components/roadmap/timeline';
import { MacroCurriculum, MesoCurriculum } from '@/services/roadmap/roadmapService';
import { fetchMacroCurriculumOffline, fetchMesoCurriculumOffline } from '@/services/roadmap/offlineRoadmapService';
import { Loader2 } from 'lucide-react';
import { OfflineWrapper } from '@/contexts/offline/components/OfflineUIComponents';

const TrainingJournalContent: React.FC = () => {
  const isMobile = useIsMobile();
  const { sessions, refreshSessions, selectedTimePeriod, setSelectedTimePeriod } = useTrainingJournal();
  const [isAddSessionOpen, setIsAddSessionOpen] = useState(false);
  
  // State for roadmap data
  const [macroData, setMacroData] = useState<MacroCurriculum[]>([]);
  const [mesoData, setMesoData] = useState<MesoCurriculum[]>([]);
  const [loadingRoadmap, setLoadingRoadmap] = useState(true);
  const [roadmapError, setRoadmapError] = useState<Error | null>(null);

  // Load sessions on component mount (removed refreshSessions from deps to prevent loops)
  useEffect(() => {
    refreshSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Load roadmap data
  const loadRoadmapData = useCallback(async () => {
    try {
      setLoadingRoadmap(true);
      setRoadmapError(null);


      const [macroResult, mesoResult] = await Promise.all([
        fetchMacroCurriculumOffline(),
        fetchMesoCurriculumOffline()
      ]);

      // Transform offline data to UI format
      const transformedMacro = macroResult
        .filter(item => Boolean(item.is_active))
        .map(item => ({ ...item, is_active: Boolean(item.is_active) }));
      const transformedMeso = mesoResult
        .filter(item => Boolean(item.is_active))
        .map(item => ({ ...item, is_active: Boolean(item.is_active) }));

      setMacroData(transformedMacro);
      setMesoData(transformedMeso);
    } catch (error) {
      console.error('Error loading curriculum data:', error);
      setRoadmapError(error as Error);
    } finally {
      setLoadingRoadmap(false);
    }
  }, []);

  useEffect(() => {
    loadRoadmapData();
  }, [loadRoadmapData]);

  const handleAddSession = () => {
    setIsAddSessionOpen(true);
  };

  const handlePeriodChange = (period: TimePeriod) => {
    if (setSelectedTimePeriod) {
      setSelectedTimePeriod(period);
    }
  };

  // Help content is now handled by MainLayout via headerUtils

  return (
    <OfflineWrapper>
      {/* MainLayout handles MobileHeader, so no need for duplicate header */}
      <div className="space-y-4 sm:space-y-6 md:space-y-8 pt-10 sm:pt-4 md:pt-8 pb-16">
        {isMobile && (
          <div className="flex justify-center mb-4">
            <AddTraining 
              variant="compact" 
              isFormOpen={isAddSessionOpen} 
              setIsFormOpen={setIsAddSessionOpen} 
            />
          </div>
        )}
        {!isMobile && (
          <TrainingJournalHeader 
            selectedPeriod={selectedTimePeriod}
            onPeriodChange={handlePeriodChange}
            onAddSession={handleAddSession}
          />
        )}
        
        {/* Zoomable Roadmap section */}
        <div className="mb-6">
          {loadingRoadmap ? (
            <div className="flex justify-center items-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading roadmap...</span>
            </div>
          ) : roadmapError ? (
            <div className="text-center p-4 border border-destructive/30 rounded-lg bg-destructive/10">
              <p className="text-destructive">Failed to load roadmap data</p>
            </div>
          ) : (
            <ZoomableTimeline macroData={macroData} mesoData={mesoData} />
          )}
        </div>
        
        <TrainingStats 
          sessions={sessions} 
          selectedPeriod={selectedTimePeriod} 
          onPeriodChange={isMobile ? handlePeriodChange : undefined}
        />
        <TabsComponent />
        <AddTraining 
          isFormOpen={isAddSessionOpen} 
          setIsFormOpen={setIsAddSessionOpen} 
        />
      </div>
    </OfflineWrapper>
  );
};

const TrainingJournalNew: React.FC = () => {
  return (
    <TrainingJournalProvider>
      <TrainingJournalContent />
    </TrainingJournalProvider>
  );
};

export default TrainingJournalNew;
